/**
 * Service for Video API calls
 */

import httpClient from "../httpClient";

class VideoApiService {
  /**
   * Get all videos from the backend
   * @returns {Promise<Array>} - Array of video objects
   */
  async getVideos() {
    try {
      const response = await httpClient.get(
        process.env.VUE_APP_BASE_URL + "/video/list",
        { retry: 3, retryDelay: 1000 }
      );

      if (response && response.data && response.data.status === 'success') {
        return response.data.videos || [];
      }

      throw new Error('Failed to get videos');
    } catch (error) {
      console.error('Error getting videos:', error);
      throw error;
    }
  }

  /**
   * Add one or more videos
   * @param {Array} videos - Array of video objects with name, youtubeId, date, year
   * @returns {Promise<Object>} - Response with results and/or errors
   */
  async addVideos(videos) {
    if (!videos || !Array.isArray(videos) || videos.length === 0) {
      throw new Error('Invalid videos array');
    }

    try {
      const response = await httpClient.post(
        process.env.VUE_APP_BASE_URL + "/video/add",
        { videos },
        { retry: 3, retryDelay: 1000 }
      );

      return response.data;
    } catch (error) {
      console.error('Error adding videos:', error);
      throw error;
    }
  }

  /**
   * Soft-delete a video (super admin only)
   * @param {number} id - Video ID
   * @param {string} userID - The Firebase UID of the super admin
   * @returns {Promise<Object>} - Response with deletion result
   */
  async deleteVideo(id, userID) {
    if (!id || !userID) {
      throw new Error('Invalid delete parameters');
    }

    try {
      const response = await httpClient.delete(
        process.env.VUE_APP_BASE_URL + "/video/delete",
        { data: { id, userID } }
      );

      return response.data;
    } catch (error) {
      console.error('Error deleting video:', error);
      throw error;
    }
  }

  /**
   * Increment view count for a video
   * @param {number} id - Video ID
   * @returns {Promise<Object>} - Response with updated view count
   */
  async incrementViews(id) {
    if (!id) {
      throw new Error('Invalid video ID');
    }

    try {
      const response = await httpClient.post(
        process.env.VUE_APP_BASE_URL + "/video/incrementViews",
        { id },
        { retry: 3, retryDelay: 1000 }
      );

      return response.data;
    } catch (error) {
      console.error('Error incrementing video views:', error);
      throw error;
    }
  }
}

export default new VideoApiService();
